import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiChevronLeft, FiChevronRight, FiType, FiMaximize2, FiMinimize2 } = FiIcons;

const splitIntoPages = (html) => {
  if (!html) return ['<p>No content available.</p>'];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;
  const pages = [];
  let currentPage = [];

  const flush = () => {
    if (currentPage.length > 0) {
      pages.push(currentPage.join(''));
      currentPage = [];
    }
  };

  Array.from(body.children).forEach(el => {
    if (el.tagName === 'H1') {
      flush();
      currentPage.push(el.outerHTML);
    } else {
      currentPage.push(el.outerHTML);
      if (currentPage.length >= 12) flush();
    }
  });
  flush();
  return pages.length > 0 ? pages : [html];
};

// Font sizes in px for the iframe body
const FONT_SIZES_PX = [14, 16, 19];
const FONT_LABELS = ['Small', 'Medium', 'Large'];

// Kindle Paperwhite portrait: 758×1024 native → display scaled down
// We use 380×513 (exactly half), maintaining 0.74:1 portrait ratio
const KINDLE_W = 380;
const KINDLE_H = 513;

const EpubDevicePreview = ({ html }) => {
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [fontSizeIdx, setFontSizeIdx] = useState(1);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDir, setFlipDir] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    const split = splitIntoPages(html);
    setPages(split);
    setCurrentPage(0);
  }, [html]);

  const goToPage = useCallback((dir) => {
    const next = currentPage + dir;
    if (next < 0 || next >= pages.length || isFlipping) return;
    setFlipDir(dir);
    setIsFlipping(true);
    setTimeout(() => {
      setCurrentPage(next);
      setIsFlipping(false);
    }, 260);
  }, [currentPage, pages.length, isFlipping]);

  const cycleFont = () => setFontSizeIdx(i => (i + 1) % 3);

  const pageContent = pages[currentPage] || '';
  const progress = pages.length > 1 ? ((currentPage + 1) / pages.length) * 100 : 100;
  const fontSize = FONT_SIZES_PX[fontSizeIdx];

  const bookStyles = `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: Georgia, 'Times New Roman', serif;
      font-size: ${fontSize}px;
      line-height: 1.85;
      color: #1a1a1a;
      background: #f8f4ef;
      padding: 24px 28px;
      word-wrap: break-word;
    }
    h1 { text-align: center; margin: 0.5em 0 0.7em; font-size: 1.35em; font-weight: bold; }
    h2 { margin: 0.4em 0 0.3em; font-size: 1.1em; }
    h3 { margin: 0.3em 0 0.25em; font-size: 1em; }
    p { margin: 0; text-indent: 1.3em; margin-bottom: 0.1em; }
    p:first-of-type, h1 + p, h2 + p, h3 + p { text-indent: 0; }
    .scene-break { text-align: center; text-indent: 0; margin: 0.8em 0; letter-spacing: 0.2em; }
    blockquote { margin: 0.6em 1.2em; font-style: italic; border-left: 2px solid #999; padding-left: 0.7em; }
    img { max-width: 100%; height: auto; display: block; margin: 0.4em auto; }
    strong, b { font-weight: bold; }
    em, i { font-style: italic; }
    a { color: inherit; text-decoration: underline; }
  `;

  const srcDoc = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>${bookStyles}</style>
</head>
<body>${pageContent}</body>
</html>`;

  const deviceW = fullscreen ? Math.min(window.innerWidth * 0.5, 480) : KINDLE_W;
  const deviceH = fullscreen ? Math.round(deviceW * (KINDLE_H / KINDLE_W)) : KINDLE_H;

  return (
    <div className={`flex flex-col items-center ${fullscreen ? 'fixed inset-0 z-50 bg-slate-900/95 p-8 overflow-auto justify-center' : ''}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between w-full mb-4 px-1" style={{ maxWidth: deviceW + 40 }}>
        <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
          Kindle Paperwhite Preview
        </span>
        <div className="flex items-center space-x-2">
          <button
            onClick={cycleFont}
            className="flex items-center space-x-1 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-bold text-slate-600 hover:text-primary transition-all"
            title={`Font size: ${FONT_LABELS[fontSizeIdx]}`}
          >
            <SafeIcon icon={FiType} />
            <span>{FONT_LABELS[fontSizeIdx]}</span>
          </button>
          <button
            onClick={() => setFullscreen(f => !f)}
            className="p-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:text-primary transition-all"
          >
            <SafeIcon icon={fullscreen ? FiMinimize2 : FiMaximize2} />
          </button>
        </div>
      </div>

      {/* Kindle device shell — portrait */}
      <div
        className="relative rounded-[24px] shadow-2xl bg-slate-800 overflow-hidden flex-shrink-0"
        style={{
          width: deviceW + 28,
          border: '10px solid #1e293b',
          boxShadow: '0 40px 80px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.07)',
        }}
      >
        {/* Top status bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-slate-700">
          <div className="flex items-center space-x-1">
            <div className="w-1.5 h-1.5 rounded-full bg-slate-500" />
            <div className="w-1.5 h-1.5 rounded-full bg-slate-500" />
          </div>
          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
            Kindle Paperwhite
          </span>
          <span className="text-[9px] font-bold text-slate-400">
            {currentPage + 1}/{pages.length}
          </span>
        </div>

        {/* E-ink screen */}
        <div
          className="relative overflow-hidden bg-[#f8f4ef]"
          style={{ height: deviceH }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={`${currentPage}-${fontSizeIdx}`}
              initial={{ opacity: 0, x: flipDir * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -flipDir * 40 }}
              transition={{ duration: 0.22, ease: 'easeInOut' }}
              className="absolute inset-0"
            >
              <iframe
                srcDoc={srcDoc}
                title={`Page ${currentPage + 1}`}
                className="w-full h-full border-0"
                style={{ colorScheme: 'light' }}
                sandbox="allow-same-origin"
              />
            </motion.div>
          </AnimatePresence>

          {/* Left tap zone */}
          <button
            onClick={() => goToPage(-1)}
            disabled={currentPage === 0}
            className="absolute left-0 top-0 h-full w-1/3 opacity-0 hover:opacity-100 flex items-center justify-start pl-2 transition-opacity disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            <div className="bg-black/20 backdrop-blur-sm rounded-full p-1">
              <SafeIcon icon={FiChevronLeft} className="text-white text-base" />
            </div>
          </button>

          {/* Right tap zone */}
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === pages.length - 1}
            className="absolute right-0 top-0 h-full w-1/3 opacity-0 hover:opacity-100 flex items-center justify-end pr-2 transition-opacity disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            <div className="bg-black/20 backdrop-blur-sm rounded-full p-1">
              <SafeIcon icon={FiChevronRight} className="text-white text-base" />
            </div>
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-0.5 bg-slate-700">
          <motion.div
            className="h-full bg-amber-400"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        {/* Bottom nav */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-700">
          <button
            onClick={() => goToPage(-1)}
            disabled={currentPage === 0}
            className="p-1.5 rounded-full hover:bg-slate-600 text-slate-300 transition-all disabled:opacity-30"
          >
            <SafeIcon icon={FiChevronLeft} className="text-sm" />
          </button>
          <span className="text-[10px] font-bold text-slate-400">
            {Math.round(progress)}% · {pages.length} pages
          </span>
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === pages.length - 1}
            className="p-1.5 rounded-full hover:bg-slate-600 text-slate-300 transition-all disabled:opacity-30"
          >
            <SafeIcon icon={FiChevronRight} className="text-sm" />
          </button>
        </div>

        {/* Home button */}
        <div className="flex justify-center py-2 bg-slate-800">
          <div className="w-8 h-8 rounded-full border-2 border-slate-600 flex items-center justify-center">
            <div className="w-3 h-3 rounded-full border-2 border-slate-500" />
          </div>
        </div>
      </div>

      {/* Page dots */}
      {pages.length > 1 && pages.length <= 20 && (
        <div className="flex items-center space-x-1 mt-4 flex-wrap justify-center gap-y-1">
          {pages.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i)}
              className={`rounded-full transition-all ${
                i === currentPage ? 'w-4 h-2 bg-primary' : 'w-2 h-2 bg-slate-300 hover:bg-slate-400'
              }`}
            />
          ))}
        </div>
      )}
      {pages.length > 20 && (
        <p className="mt-3 text-xs text-slate-400 font-medium">
          Page {currentPage + 1} of {pages.length} — tap edges to turn pages
        </p>
      )}
    </div>
  );
};

export default EpubDevicePreview;